"use client";

import { useState, useEffect } from 'react';

interface TOCItem {
    id: string;
    title: string;
    level: 'h2' | 'h4';
}

interface TableOfContentsProps {
    subject: any;
}

export function TableOfContents({ subject }: TableOfContentsProps) {
    const [tocItems, setTocItems] = useState<TOCItem[]>([]);
    const [activeSection, setActiveSection] = useState<string>('');

    useEffect(() => {
        // Extract headings from subject.questions_answers or contentSections
        const items: TOCItem[] = [];
        
        if (subject.questions_answers) {
            subject.questions_answers.forEach((item: any[], index: number) => {
                if (item.length > 0 && item[0]) {
                    const title = item[0];
                    const id = `section-${index}`;
                    items.push({ id, title, level: 'h2' });
                }
            });
        }
        
        // For case studies with structured content - only show H2 headings
        if (subject.contentSections) {
            subject.contentSections.forEach((section: any, sectionIndex: number) => {
                // Use the actual ID from the content block
                items.push({ 
                    id: section.id, 
                    title: section.heading, 
                    level: 'h2' 
                });
            });
        }
        
        setTocItems(items);
        
        // Set the first section as active initially
        if (items.length > 0) {
            setActiveSection(items[0].id);
        }
    }, [subject]);

    useEffect(() => {
        const observerOptions = {
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0
        };

        const observer = new IntersectionObserver((entries) => {
            // Find all currently intersecting sections
            const intersectingSections = entries
                .filter(entry => entry.isIntersecting)
                .map(entry => entry.target.id);

            if (intersectingSections.length > 0) {
                // If multiple sections are intersecting, select the first one
                // (which should be the topmost one due to the rootMargin)
                setActiveSection(intersectingSections[0]);
            } else {
                // If no sections are intersecting, find the section that's closest to the top
                const allSections = tocItems.map(item => ({
                    id: item.id,
                    element: document.getElementById(item.id)
                })).filter(section => section.element);

                if (allSections.length > 0) {
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    let closestSection = allSections[0];
                    let closestDistance = Math.abs(allSections[0].element!.offsetTop - scrollTop);

                    allSections.forEach(section => {
                        const distance = Math.abs(section.element!.offsetTop - scrollTop);
                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestSection = section;
                        }
                    });

                    setActiveSection(closestSection.id);
                }
            }
        }, observerOptions);

        // Observe all sections
        tocItems.forEach(({ id }) => {
            const element = document.getElementById(id);
            if (element) {
                observer.observe(element);
            }
        });

        return () => observer.disconnect();
    }, [tocItems]);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const headerOffset = 100; // Account for any fixed headers
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    if (tocItems.length === 0) {
        return null;
    }

    return (
        <div className="lg:sticky lg:top-4 w-full rounded-2xl bg-ss-lightest-blue p-4 lg:p-4 xl:p-6 shadow-sm border border-gray-100">
            <div className="mb-4 lg:mb-4 xl:mb-6">
                <p className="px-3 font-semibold text-sm">In this article</p>
            </div>
            <nav className="lg:space-y-2 lg:block">
                {/* Mobile: horizontal scroll */}
                <div className="flex gap-2 overflow-x-auto pb-2 lg:hidden">
                    {tocItems.map(({ id, title }) => (
                        <button
                            key={id}
                            onClick={() => scrollToSection(id)}
                            className={`shrink-0 rounded-lg px-3 py-2 text-xs transition-all duration-200 ${
                                activeSection === id
                                    ? 'bg-gray-200 text-gray-900 font-medium'
                                    : 'bg-white text-gray-700 hover:text-gray-900'
                            }`}
                        >
                            <span className="whitespace-nowrap">{title}</span>
                        </button>
                    ))}
                </div>
                
                {/* Desktop: simple vertical list */}
                <ol className="hidden lg:block space-y-2 xl:space-y-3">
                    {tocItems.map(({ id, title }) => (
                        <li
                            key={id}
                            onClick={() => scrollToSection(id)}
                            className={`cursor-pointer text-base font-serif list-none font-semibold p-3 lg:p-3 xl:p-4 transition-all duration-200 hover:bg-white rounded-3xl ${
                                activeSection === id
                                    ? 'bg-white text-gray-900'
                                    : 'text-gray-700 hover:text-gray-900'
                            }`}
                        >
                            {title}
                        </li>
                    ))}
                </ol>
            </nav>
        </div>
    );
}
